import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Drawer.css';

export interface DrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when drawer should close */
  onClose: () => void;
  /** Drawer title */
  title?: string;
  /** Drawer content */
  children: React.ReactNode;
  /** Footer content (typically buttons) */
  footer?: React.ReactNode;
  /** Drawer position */
  position?: 'left' | 'right';
  /** Drawer width */
  width?: string | number;
  /** Additional CSS classes */
  className?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  position = 'right',
  width = 400,
  className = '',
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  const classNames = [
    'ui-drawer',
    `ui-drawer--${position}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const widthStyle = typeof width === 'number' ? `${width}px` : width;

  return createPortal(
    <div
      className="ui-drawer-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'drawer-title' : undefined}
    >
      <div ref={drawerRef} className={classNames} style={{ width: widthStyle }}>
        {title && (
          <div className="ui-drawer__header">
            <h2 id="drawer-title" className="ui-drawer__title">
              {title}
            </h2>
            <button
              type="button"
              className="ui-drawer__close"
              onClick={onClose}
              aria-label="Close drawer"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
        <div className="ui-drawer__content">{children}</div>
        {footer && <div className="ui-drawer__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

Drawer.displayName = 'Drawer';
